import * as React from 'react';
import { format, parse, isValid, parseISO, subMonths, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  value: string;          // YYYY-MM-DD or ''
  onChange: (val: string) => void;
  className?: string;
  disabled?: boolean;
}

const today = () => new Date();

const PRESETS = [
  { label: 'Today',           date: () => today() },
  { label: '1 month ago',     date: () => subMonths(today(), 1) },
  { label: '2 months ago',    date: () => subMonths(today(), 2) },
  { label: 'In 1 month',      date: () => addMonths(today(), 1) },
  { label: 'In 2 months',     date: () => addMonths(today(), 2) },
  { label: 'Start of month',  date: () => startOfMonth(today()) },
  { label: 'End of month',    date: () => endOfMonth(today()) },
];

export function DatePicker({ value, onChange, className, disabled }: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputVal, setInputVal] = React.useState(
    value ? format(parseISO(value), 'dd/MM/yyyy') : '',
  );
  const [month, setMonth] = React.useState<Date>(
    value ? parseISO(value) : new Date(),
  );

  React.useEffect(() => {
    setInputVal(value ? format(parseISO(value), 'dd/MM/yyyy') : '');
  }, [value]);

  const selected = value ? parseISO(value) : undefined;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    let raw = e.target.value.replace(/[^\d/]/g, '');
    const digits = raw.replace(/\//g, '');
    if (digits.length <= 2) raw = digits;
    else if (digits.length <= 4) raw = digits.slice(0, 2) + '/' + digits.slice(2);
    else raw = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8);
    setInputVal(raw);
    if (digits.length === 8) {
      const parsed = parse(raw, 'dd/MM/yyyy', new Date());
      if (isValid(parsed)) onChange(format(parsed, 'yyyy-MM-dd'));
    } else if (digits.length === 0) {
      onChange('');
    }
  }

  function handleInputBlur() {
    const parsed = parse(inputVal, 'dd/MM/yyyy', new Date());
    if (isValid(parsed) && inputVal.length === 10) {
      onChange(format(parsed, 'yyyy-MM-dd'));
    } else if (!inputVal) {
      onChange('');
    } else {
      setInputVal(value ? format(parseISO(value), 'dd/MM/yyyy') : '');
    }
  }

  function applyPreset(date: Date) {
    onChange(format(date, 'yyyy-MM-dd'));
    setMonth(date);
    setOpen(false);
  }

  return (
    <div className={cn('flex w-full overflow-hidden rounded-lg border border-border bg-background focus-within:border-primary focus-within:ring-1 focus-within:ring-ring', className)}>
      <input
        type="text"
        value={inputVal}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder="dd/mm/yyyy"
        disabled={disabled}
        maxLength={10}
        className="flex-1 bg-transparent py-2.5 pl-3.5 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            tabIndex={-1}
            className="flex items-center border-l border-border px-3 text-muted-foreground hover:bg-muted/40 hover:text-foreground focus:outline-none disabled:opacity-50"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: 400 }} align="end">
          <div className="flex">
            {/* Calendar */}
            <Calendar
              mode="single"
              selected={selected}
              month={month}
              onMonthChange={setMonth}
              onSelect={(date) => {
                onChange(date ? format(date, 'yyyy-MM-dd') : '');
                setOpen(false);
              }}
              fixedWeeks
              autoFocus
              className="w-[260px] shrink-0"
            />
            {/* Presets sidebar */}
            <div className="flex flex-col gap-0.5 border-l border-border p-2 w-[140px] shrink-0">
              <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Quick pick</p>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p.date())}
                  className="rounded-md px-2.5 py-1.5 text-left text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
